import axios from 'axios';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from "react";
import { NavLink, useNavigate } from 'react-router-dom';
// @mui
import { LoadingButton } from '@mui/lab';
import { Check, Close, ControlPoint, Delete, Edit, RemoveCircleOutline, Save } from '@mui/icons-material';
import { alpha, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
// hooks
import useAppContext from "src/hooks/useAppContext";
import useResponsive from 'src/hooks/useResponsive';
//
import { WEB_API } from 'src/config';
import { PATHS } from 'src/routes/paths';
import { toCurrency } from 'src/utils/number';
import { ORDER_TYPE, PAYMENT_METHODS } from 'src/mocks/constants';

function Ingredients({ data, onClose }) {
  const theme = useTheme();
  const [ingredients, setIngredients] = useState([]);
  const { updateCart } = useAppContext();

  useEffect(() => {
    const ingredients = data.ingredients?.map(e => {
      // check if e is array
      if (Array.isArray(e)) {
        return e.map(i => i.trim().replace('.', ''));
      }
      return e.trim().replace('.', '');
    });
    setIngredients(Array.from(new Set(ingredients.flat()))?.map(i => ({ name: i, removed: data.removed?.includes(i) })));
  }, [data]);

  const save = () => {
    updateCart(data.localId, { removed: ingredients.flatMap(e => e.removed ? [e.name] : []) });
    onClose();
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      onClose={onClose}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16, bgcolor: theme.palette.grey[300] }}
      >
        <Close />
      </IconButton>

      <DialogTitle>
        {data.name}
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        {/* <Typography variant="h5">
          {data.name}
        </Typography> */}

        {ingredients.map(e =>
          <Stack key={e.name} direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
            <Typography>
              {e.name}
            </Typography>

            <FormControlLabel
              value="end"
              control={<Switch color="primary" checked={!e.removed} />}
              onChange={() => {
                setIngredients(prev => prev.map(i => {
                  if (i.name === e.name)
                    return { ...i, removed: !i.removed };
                  return i;
                }))
              }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          color="success"
          variant="contained"
          startIcon={<Save />}
          onClick={save}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Additional({ data, availableAdditional, onClose }) {
  const theme = useTheme();
  const [additional, setAdditional] = useState(data.additional?.reduce((p, c) => ({ ...p, [c.id]: c }), {}) || {});

  const { updateCart } = useAppContext();

  const save = () => {
    const add = Object.values(additional).filter(e => e.q);
    updateCart(data.localId, { additional: add.map(e => ({ id: e.id, q: e.q, price: e.price, name: e.name })) });
    onClose();
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      onClose={onClose}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16, bgcolor: theme.palette.grey[300] }}
      >
        <Close />
      </IconButton>

      <DialogTitle>
        Adicionais
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        {availableAdditional?.map(e =>
          <Stack key={e.name} direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <IconButton
                disabled={Number(additional[e.id]?.q || 0) <= 0}
                onClick={() => setAdditional(prev => ({ ...prev, [e.id]: { ...e, q: (prev[e.id]?.q || 0) - 1 } }))}
                sx={{ width: 20, height: 20, p: 0, bgcolor: theme.palette.primary.main, color: 'white' }}
              >
                <RemoveCircleOutline />
              </IconButton>

              <TextField
                size="small"
                value={additional[e.id]?.q || 0}
                onChange={(event) => {
                  const n = Number(event.target.value.replace(/\D/g, '')) || '';
                  if (n > 0 || n === '')
                    setAdditional(prev => ({ ...prev, [e.id]: { ...e, q: n } }));
                }}
                sx={{ width: 50, flexShrink: 0 }}
              />

              <IconButton
                disabled={Number(additional[e.id]?.q || 0) >= Number(e.q)}
                onClick={() => setAdditional(prev => ({ ...prev, [e.id]: { ...e, q: (prev[e.id]?.q || 0) + 1 } }))}
                sx={{ width: 20, height: 20, p: 0, bgcolor: theme.palette.primary.main, color: 'white' }}
              >
                <ControlPoint />
              </IconButton>

              <Box>
                <Typography variant='h6' sx={{ ml: 2 }}>
                  {e.name}
                </Typography>

                <Typography variant='body2' sx={{ ml: 2 }}>
                  {/* {e.price ? `${toCurrency((additional[e.id]?.q || 0) * Number(e.price))}` : ''} */}
                  {toCurrency(Number(e.price))}
                </Typography>
              </Box>
            </Stack>

          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          color="success"
          variant="contained"
          startIcon={<Save />}
          onClick={save}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ItemDialog({ onClose, data }) {
  const [secondFlavor, setSecondFlavor] = useState((data.flavors?.length || 0) > 1);
  const [flavors, setFlavors] = useState(data.flavors?.reduce((p, c, idx) => ({ ...p, [idx]: c }), {}));

  const { updateCart } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const isDesktop = useResponsive('up', 'md');

  const ingredients = data.availableFlavors?.reduce((p, c, idx) => ({ ...p, [c]: data.availableIngredients?.[idx] || [] }), {}) || {};

  const save = () => {
    updateCart(data.localId, { flavors: Object.values(flavors).sort(), ingredients: Object.values(flavors)?.sort().map(f => ingredients[f] || []) });
    onClose();
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      onClose={onClose}
    >
      <IconButton
        size="small"
        color="default"
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        <Typography variant="h5">
          {data.name}
        </Typography>

        {Boolean(data.availableFlavors?.length) &&
          <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
              <InputLabel>{secondFlavor ? 'Selecione o primeiro sabor' : 'Selecione o sabor'}</InputLabel>
              <Select
                fullWidth
                value={flavors[0] ?? ''}
                onChange={(e) => setFlavors(prev => ({ ...prev, 0: e.target.value }))}
                native={!isDesktop}
                label={secondFlavor ? 'Selecione o primeiro sabor' : 'Selecione o sabor'}
              >
                {(!isDesktop) && (
                  <option value="" disabled />
                )}
                {data.availableFlavors.map(i =>
                  isDesktop ? (
                    <MenuItem key={i} value={i}>{i}</MenuItem>
                  )
                    : <option key={i} value={i}>{i}</option>
                )}
              </Select>

              <Typography variant="body2" sx={{ mt: 0.5, ml: 1 }}>
                {ingredients[flavors[0]]?.join(', ')}
              </Typography>
            </FormControl>

            {data.nFlavors && Number(data.nFlavors) > 1 &&
              <Stack direction={'row'} justifyContent={'center'} sx={{ mt: 2, width: '100%' }}>
                {secondFlavor ?
                  <FormControl fullWidth>
                    <InputLabel>Selecione o segundo sabor</InputLabel>
                    <Select
                      fullWidth
                      value={flavors[1] ?? ''}
                      onChange={(e) => setFlavors(prev => ({ ...prev, 1: e.target.value }))}
                      native={!isDesktop}
                      label={'Selecione o segundo sabor'}
                    >
                      {(!isDesktop) && (
                        <option value="" disabled />
                      )}
                      {data.availableFlavors.map(i =>
                        isDesktop ? (
                          <MenuItem key={i} value={i}>{i}</MenuItem>
                        )
                          : <option key={i} value={i}>{i}</option>
                      )}
                    </Select>

                    <Typography variant="body2" sx={{ mt: 0.5, ml: 1 }}>
                      {ingredients[flavors[1]]?.join(', ')}
                    </Typography>
                  </FormControl>
                  :
                  <Button
                    variant="contained"
                    onClick={() => setSecondFlavor(true)}
                  >
                    Quero dois sabores
                  </Button>
                }
              </Stack>
            }
          </Box>
        }
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            if (!Object.values(flavors)?.length) {
              enqueueSnackbar("Escolha pelo menos um sabor!", { variant: 'warning' });
              return;
            }
            save();
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog >
  )
}

Product.propTypes = {
  data: PropTypes.object,
  additional: PropTypes.any
}
function Product({ data, additional }) {
  // const [cover, setCover] = useState(null);
  const [itemDialog, setItemDialog] = useState(null);
  const [additionalDialog, setAdditionalDialog] = useState(null);
  const [ingredientsDialog, setIngredientsDialog] = useState(null);

  const { updateCart, removeCartItem } = useAppContext();

  const theme = useTheme();

  // useEffect(() => {
  //   if (!data.cover || cover)
  //     return

  //   const key = `uploads/store/cover/${data.cover}`;
  //   Storage.get(key, {
  //     download: true,
  //     level: 'public',
  //     cacheControl: 'no-cache'
  //   }).then(resp => {
  //     const img = URL.createObjectURL(resp.Body);
  //     setCover(img);
  //   }).catch(e => {
  //     console.log(e);
  //   });
  //   // eslint-disable-next-line
  // }, [data]);

  return (
    <Card key={data.localId} sx={{ width: '100%', display: 'flex', bgcolor: alpha(theme.palette.background.default, 0.1) }}>
      {/* {cover ?
        <CardMedia
          component="img"
          image={cover}
          alt="item"
          sx={{ width: { xs: '30%', lg: '20%' } }}
        />
        :
        <Stack direction='row' justifyContent='center' sx={{ width: { xs: '30%', lg: '20%' } }}>
          <img
            src={'/icons/image.svg'}
            alt="item"
            style={{ width: '60%' }}
          />
        </Stack>
      } */}

      <CardContent sx={{ width: '100%' }}>  {/* width: { xs: '70%', lg: '80%' }*/}
        <Stack direction={'row'} justifyContent={'space-between'} sx={{ width: '100%', mb: 1 }}>
          <Typography variant="h5">
            {data.name}
          </Typography>

          <Typography textAlign='right' variant="h6">
            {toCurrency(Number(data.price))}
          </Typography>
        </Stack>

        {data.flavors?.length &&
          <Typography variant="body1">
            Sabor(es): <b>{data.flavors?.map((e, i) => `${e} (${data.ingredients?.[i]?.join(', ')})`).join(', ')}</b>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Edit sx={{ fontSize: 16 }} />}
              onClick={() => setItemDialog(data)}
              sx={{ ml: 1 }}
            >
              Alterar sabores
            </Button>
          </Typography>
        }

        {!data.flavors?.length &&
          <Typography variant="body1">
            {data.ingredients?.join(', ')}
          </Typography>
        }

        <Stack direction={'row'} spacing={1} sx={{ my: 1 }}>
          {additional &&
            <Button
              variant='outlined'
              onClick={() => setAdditionalDialog(true)}
            >
              Adicionais
            </Button>
          }

          {data.ingredients &&
            <Button
              color='error'
              variant='outlined'
              onClick={() => setIngredientsDialog(data)}
            >
              Remover Ingredientes
            </Button>
          }
        </Stack>

        {data.removed &&
          <Typography>
            <b>Ingredientes removidos:</b> {data.removed.join(', ')}
          </Typography>
        }

        {data.additional?.length &&
          <Typography>
            <b>Adicionais:</b> {data.additional.map(e => `${e.q}x ${e.name} (${toCurrency((e.q) * Number(e.price))})`).join(', ')}
          </Typography>
        }

        <Typography variant="body1" sx={{ mt: 0.5 }}>
          Quantidade:
        </Typography>

        <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <IconButton
              disabled={Number(data.q) === 1}
              onClick={() => {
                if (data.q > 1)
                  updateCart(data.localId, { q: data.q - 1 });
              }}
              sx={{ width: 20, height: 20, p: 0, bgcolor: theme.palette.primary.main, color: 'white' }}
            >
              <RemoveCircleOutline />
            </IconButton>

            <TextField
              size="small"
              value={data.q}
              onChange={(event) => {
                const n = Number(event.target.value.replace(/\D/g, '')) || '';
                if (n > 0 || n === '')
                  updateCart(data.localId, { q: n });
              }}
              sx={{ width: 50 }}
            />

            <IconButton
              disabled={Number(data.q) >= Number(data.availableQtd)}
              onClick={() => updateCart(data.localId, { q: data.q + 1 })}
              sx={{ width: 20, height: 20, p: 0, bgcolor: theme.palette.primary.main, color: 'white' }}
            >
              <ControlPoint />
            </IconButton>
          </Stack>

          {Number(data.availableQtd) < Number(data.q) &&
            <Typography textAlign={'center'} color={'red'}>Quantidade disponível: {data.availableQtd}</Typography>
          }
        </Stack>

        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <TextField
            multiline
            maxRows={2}
            label='Observações'
            value={data.obs}
            onChange={(e) => {
              updateCart(data.localId, { obs: e.target.value });
            }}
            sx={{ mt: 1, width: { xs: '80%', sm: '50%' } }}
          />

          <Tooltip title='Remover'>
            <IconButton
              size='large'
              onClick={() => removeCartItem(data.localId)}
              sx={{ color: theme.palette.error.main, width: 'fit-content' }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* <Typography variant="body1" sx={{ mt: 1 }}>
            {!data?.q || Number(data?.q) <= 0 ?
              'Indisponível!'
              :
              <>Quantidade disponível: <b>{data.q}</b></>
            }
          </Typography> */}
      </CardContent>

      {ingredientsDialog &&
        <Ingredients data={ingredientsDialog} onClose={() => setIngredientsDialog(null)} />
      }

      {itemDialog &&
        <ItemDialog
          data={itemDialog}
          // setAdded={setAdded}
          onClose={() => setItemDialog(null)}
        />
      }

      {additionalDialog &&
        <Additional
          data={data}
          availableAdditional={additional}
          onClose={() => setAdditionalDialog(false)}
        />
      }
    </Card>
  )
}

export default function Cart() {
  // const [loading, setLoading] = useState(true);
  // eslint-disable-next-line
  const [error, setError] = useState(null);
  const [additional, setAdditional] = useState(null);
  const [type, setType] = useState(ORDER_TYPE.DELIVERY);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'md');
  const { user, cart, deliveryStatus, givenTime, updateCartMultiple, cleanCart, getIdToken } = useAppContext();

  useEffect(() => {
    if (cart.every(e => e.category !== 'LANCHES'))
      return;

    axios.request({
      url: `${WEB_API}/list_additional`,
      method: 'GET',
      headers: {
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*'
      },
    }).then((resp) => setAdditional(resp.data))
  }, [cart])

  const handleOrder = async () => {
    if (!user) {
      navigate(PATHS.login);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      if (cart.some(e => !e.q || (e.availableQtd && Number(e.q) > Number(e.availableQtd)))) {
        setError("Todos os itens devem ter uma quantidade válida!");
        setIsSubmitting(false);
        return;
      }

      if (!paymentMethod) {
        setError("Escolha uma forma de pagamento");
        setIsSubmitting(false);
        return;
      }

      cart.forEach((item) => {
        delete item.ingredients;
        delete item.availableDelivery;
        delete item.availableQtd;
        delete item.availableFlavors;
        delete item.availableIngredients;
      })

      const data = {
        Items: cart,
        ...!user?.email && {
          name: user.name,
          userId: user.userId,
          phone: user.phone,
          address: {
            street: user.street,
            district: user.district,
            ref: user.ref
          },
        },
        time: givenTime,
        hour: Date.now(),
        type,
        paymentMethod
      }

      let params = {};
      if (user?.email) {
        const token = await getIdToken(true);
        params = { Authorization: `Bearer ${token}` }
      }

      const resp = await axios.request({
        url: `${WEB_API}/put_order`,
        method: 'POST',
        data,
        headers: {
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          ...user?.email && params
        },
      })

      if (resp.data.cod === 400) {
        setIsSubmitting(false);
        setError(resp.data.message);
        updateCartMultiple(resp.data.qtds);
        return;
      }

      // enqueueSnackbar("Pedido realizado com sucesso!");

      cleanCart();
      setIsSubmitting(false);
      navigate(PATHS.orders);
    } catch (e) {
      console.log(e);
      setError("Ocorreu um erro ao registrar o pedido! Por favor, tente novamente.");
      setIsSubmitting(false);
    }
  }

  return (
    <Card sx={{ p: 2, m: 2, borderRadius: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Meu Carrinho
      </Typography>

      {cart.length ?
        <>
          <Stack spacing={1} direction='column' sx={{ width: '100%' }}>
            {cart.map(e =>
              <Product key={e.localId} data={e} additional={e.category === 'LANCHES' ? additional : null} />
            )}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ mt: 2 }}>
            {user &&
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <Typography variant='h6'>
                  Endereço
                </Typography>

                <Typography>
                  {user?.name}
                </Typography>
                <Typography>
                  {user?.street}
                </Typography>
                <Typography>
                  {user?.district}
                </Typography>
                <Typography>
                  {user?.ref}
                </Typography>

                <Button
                  variant='outlined'
                  startIcon={<Edit sx={{ fontSize: 16 }} />}
                  onClick={() => navigate('/login')}
                  sx={{ mt: 1 }}
                >
                  Alterar endereço
                </Button>
              </Box>
            }

            <Stack direction='column' alignItems='end' sx={{ width: user ? { xs: '100%', md: '50%' } : '100%' }}>
              <Typography variant="body1">
                Total do Pedido:
              </Typography>

              <Typography variant="h6">
                {(() => {
                  const items = cart?.reduce((p, c) => p + (Number(c.price) * Number(c.q)), 0) || 0;
                  const additionals = cart?.flatMap(i => i.additional || []).reduce((p, c) => p + (Number(c.price) * Number(c.q)), 0) || 0;
                  return toCurrency(items + additionals);
                })()}
              </Typography>

              <Typography variant="body1">
                Taxa de Entrega:
              </Typography>

              {user?.district &&
                <Typography variant="h6">
                  + {deliveryStatus?.districts?.find(e => e.name === user.district)?.tax}
                </Typography>
              }

              <FormControl sx={{ textAlign: 'right', my: 1 }}>
                <FormLabel>Forma de entrega</FormLabel>
                <RadioGroup
                  row
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <FormControlLabel value={ORDER_TYPE.DELIVERY} control={<Radio />} label="Entrega" />
                  <FormControlLabel value={ORDER_TYPE.PICK_UP} control={<Radio />} label="Retirar no Local" sx={{ mr: 0 }} />
                </RadioGroup>
              </FormControl>

              <FormControl sx={{ mb: 1, width: { xs: '100%', md: '70%' } }}>
                <InputLabel>{'Selecione a forma de pagamento'}</InputLabel>
                <Select
                  fullWidth
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  native={!isDesktop}
                  label={'Selecione a forma de pagamento'}
                >
                  {(!isDesktop) && (
                    <option value="" disabled />
                  )}
                  {PAYMENT_METHODS.map(i =>
                    isDesktop ? (
                      <MenuItem key={i.key} value={i.key}>{i.label}</MenuItem>
                    )
                      : <option key={i.key} value={i.key}>{i.label}</option>
                  )}
                </Select>
              </FormControl>

              {paymentMethod === 'PIX' &&
                <>
                  {/* <Typography fontSize={20} display={'flex'} alignItems={'center'} sx={{ mb: 1 }}>
                    Chave PIX: &nbsp;<span style={{ textDecoration: 'underline' }}>27997791499</span>
                    <Tooltip title='Copiar chave PIX'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          navigator.clipboard.writeText('27997791499');
                        }}
                        sx={{
                          ml: 1,
                          color: 'white',
                          bgcolor: theme.palette.primary.main,
                          ':hover': { backgroundColor: theme.palette.primary.light },
                        }}
                      >
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Typography> */}

                  <Typography>
                    {/* Envie o comprovante de pagamento para o nosso WhatsApp após realizar o pedido:
                    <Button
                      startIcon={<WhatsApp />}
                      sx={{ ml: 1 }}
                    >
                      27 997791499
                    </Button>
                    <br /> */}
                    Os dados para pagamento estarão disponíveis na tela "Meus Pedidos" após a finalização do pedido.
                  </Typography>
                </>
              }

              <Stack direction={'row'} justifyContent={'right'} alignItems={'center'} spacing={2}>
                <Typography color={theme.palette.error.dark} variant='h6'>
                  {error}
                </Typography>

                {!deliveryStatus?.open ?
                  <Typography variant='body2' color={theme.palette.error.light}>
                    Não é possível realizar pedidos neste momento. Verifique os horários de funcionamento
                  </Typography>
                  :
                  (user ?
                    <LoadingButton
                      variant='contained'
                      startIcon={<Check />}
                      loading={isSubmitting}
                      onClick={handleOrder}
                      disabled={!deliveryStatus?.open}
                      sx={{ width: 'fit-content', mt: 1 }}
                    >
                      Realizar Pedido
                    </LoadingButton>
                    :
                    <Button
                      variant="contained"
                      component={NavLink}
                      to='/login?redirect=carrinho'
                      sx={{ mt: 1 }}
                    >
                      Fazer pedido
                    </Button>
                  )
                }
              </Stack>
            </Stack>
          </Stack>
        </>
        :
        (user === undefined ?
          <CircularProgress />
          :
          <Typography variant="body1" fontSize={20} textAlign='center' color={theme.palette.grey[400]} sx={{ width: '100%' }}>
            Carrinho vazio
          </Typography>
        )
      }
    </Card>
  )
}